/**
 * 既知のエラーコードに対する画面表示用の日本語文言。
 * バックエンドが `message_ja` を返さない/まだ実装していない場合の補完。
 * backend が message_ja を返した場合はそちらが優先される（このマップは fallback 扱い）。
 */
const KNOWN_CODE_MESSAGES: Record<string, string> = {
  SIM_ALREADY_IN_USE: '他のユニットで使用中です',
  SERIAL_NUMBER_DUPLICATED: '他のユニットで使用中です',
};

/**
 * API エラーからユーザー向けの日本語メッセージを取り出す。
 *
 * ecois-api のエラーレスポンス形式: `{ error: { code, message, message_ja, details } }`
 * フロントは `message_ja` を主表示に使う（HTTP status は補助）。
 *
 * - レスポンスが読めない場合 = 通信失敗（オフライン）/ CORS でブロックされた 5xx 等
 * - レスポンスがある場合 = `error.message_ja` を優先、未提供時は既知 code でも補完
 *
 * @param fallback `message_ja` も既知 code も無い場合に表示する文言。画面固有の文言を渡せる。
 */
function getApiErrorMessage(err: any, fallback = 'エラーが発生しました'): string {
  // err.response が無い = レスポンス本文を読めないケース。オフライン等の通信失敗に加え、
  // 未捕捉の 500 / タイムアウト 504 が CORS ミドルウェアより外側で生成され Access-Control-Allow-Origin
  // が欠落してブラウザにブロックされた場合も含む。いずれも内容を判別できないため、
  // axios の英語 "Network Error" を出さず、システム管理者への問い合わせを促す日本語文言を表示する。
  if (!err?.response) {
    return '通信に失敗しました。時間をおいて再度お試しいただき、解決しない場合はシステム管理者にお問い合わせください。';
  }

  // 実 API は `{ error: { code, message, message_ja, ... } }` 形式。
  // message_ja を優先し、無ければ既知 code → message → fallback の順で補完。
  // 念のためトップレベル (error ラッパー無し) の形状にもフォールバックする。
  const data = err.response.data;
  const code: string | undefined = data?.error?.code ?? data?.code;
  return (
    data?.error?.message_ja ||
    data?.message_ja ||
    (code && KNOWN_CODE_MESSAGES[code]) ||
    data?.error?.message ||
    data?.message ||
    fallback
  );
}

export { getApiErrorMessage };
